import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ShieldCheck } from 'lucide-react';
import { IITH } from '@/pages/landing/iithBrand';

/**
 * Step 2 of OTP auth — enter code from email (or optional on-screen demo OTP).
 */
export default function OtpChallengePanel({
  challenge,
  onVerify,
  onBack,
  verifying = false,
  accent = IITH.saffron,
}) {
  const [otp, setOtp] = useState('');
  const len = challenge?.otp_length || 6;
  const onScreen = Boolean(challenge?.dev_otp) || challenge?.delivery === 'on_screen';

  const submit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if ((otp || '').trim().length < 4) return;
    onVerify(otp.trim());
  };

  return (
    <div className="space-y-4" data-testid="otp-challenge-panel">
      <div className="flex items-start gap-3 rounded-lg border px-3 py-3 bg-zinc-50">
        <ShieldCheck className="h-5 w-5 mt-0.5 shrink-0" style={{ color: accent }} />
        <div className="text-sm text-zinc-700">
          <p className="font-medium text-zinc-900">Verify it’s you</p>
          <p className="mt-1 text-xs text-zinc-500">
            {onScreen
              ? 'Use the one-time password shown below. It is not sent by email.'
              : (
                <>
                  Enter the OTP sent to{' '}
                  {[challenge?.masked_email, challenge?.masked_phone].filter(Boolean).join(' · ') || 'your contact'}
                  .
                </>
              )}
            {' '}Expires in {challenge?.expires_in || 300}s.
          </p>
        </div>
      </div>

      {challenge?.dev_otp ? (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-center"
          data-testid="dev-otp-display"
        >
          <p className="text-[11px] uppercase tracking-wide text-amber-800">Your OTP</p>
          <p className="text-2xl font-semibold tracking-[0.35em] text-amber-950 mt-1">
            {challenge.dev_otp}
          </p>
        </div>
      ) : null}

      <div className="space-y-1.5">
        <Label>One-time password</Label>
        <Input
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={len}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, len))}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(e); }}
          placeholder={`${len}-digit code`}
          className="h-12 text-center text-lg tracking-[0.4em]"
          data-testid="otp-input"
          required
        />
      </div>

      <Button
        type="button"
        onClick={submit}
        disabled={verifying || otp.length < 4}
        className="w-full h-11 text-white"
        style={{ background: accent }}
        data-testid="otp-verify-submit"
      >
        {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
        Verify & continue
      </Button>
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="w-full text-sm text-zinc-500 hover:text-zinc-800"
          data-testid="otp-back"
        >
          Back
        </button>
      ) : null}
    </div>
  );
}
